import React, { useCallback, useMemo, useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert, Tooltip } from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import PropTypes from 'prop-types';
import { useAuth } from '../context/AuthContext';

/**
 * Run the universe scan on demand — but only if it would actually produce something new.
 *
 * <h2>It asks before it spends</h2>
 *
 * A global scan is minutes of ephemeris across hundreds of symbols. Running one that would
 * reproduce the file already on disk is pure cost, so the button calls the status endpoint first
 * and refuses the work when today's scan already covers the newest data held. The user gets a
 * snackbar saying so rather than a progress spinner that ends where it started.
 *
 * <h2>The check is on the SERVER, and the button only reports it</h2>
 *
 * Both conditions — does a file exist for today, and has a bar arrived since — are answered by the
 * backend, which is the only side that can see the scan store and the price files. A frontend that
 * decided this for itself would be guessing from whatever it had cached, and would guess wrong
 * exactly when the two disagreed, which is the only case that matters.
 *
 * <p>`force` exists for the case the check cannot cover: a scan whose RULES changed. The engine's
 * parameters are not in the file, so an unchanged universe and an unchanged date still produce a
 * different answer after a threshold moves. The button offers it as a second, explicit press
 * rather than as a default, because the default should be the cheap one.
 */
const GlobalScanButton = ({ marketId, onComplete, onError, variant, size }) => {
  const { api, token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  // Memoised. A fresh object every render would change the callback's identity every render,
  // which is the same defect that made the scan viewer's fetch effect cancel its own request on
  // a loop - cheap to avoid, invisible once it bites.
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const run = useCallback(async (force = false) => {
    if (busy) return;
    setBusy(true);
    setToast(null);
    // The scan is filed under the market's trading day. Taken here rather than on the server so a
    // user in one timezone and a server in another agree about which day they mean.
    const today = new Date().toISOString().slice(0, 10);
    try {
      if (!force) {
        const status = await api.get(
          `/api/integral-delta/scans/${today}/status`
            + (marketId ? `?marketId=${marketId}` : ''), auth);
        if (status?.data?.upToDate) {
          setToast({
            severity: 'info',
            message: `Scan already up to date — ${status.data.signalCount} triggers from `
              + `${status.data.generatedAt || 'the stored scan'}.`,
            allowForce: true,
          });
          return;
        }
      }

      const res = await api.post('/api/integral-delta/scans/run',
        { date: today, ...(marketId ? { marketId } : {}) }, auth);
      const d = res?.data || {};
      setToast({
        severity: 'success',
        message: `Scan complete — ${d.signalCount} triggers across `
          + `${d.symbolsWithSignals} of ${d.universeSize} symbols`
          + (d.staleSymbolsExcluded ? `, ${d.staleSymbolsExcluded} excluded as stale` : '')
          + `. ${Math.round((d.elapsedMillis || 0) / 1000)}s.`,
      });
      if (onComplete) onComplete(d);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'The global scan failed.';
      setToast({ severity: 'error', message });
      if (onError) onError(message);
    } finally {
      // In a finally, so a thrown request cannot leave the button spinning forever — which on a
      // multi-minute scan looks identical to one that is still working.
      setBusy(false);
    }
  }, [api, auth, busy, marketId, onComplete, onError]);

  return (
    <>
      <Tooltip arrow placement="bottom" title={busy
        ? 'Scanning the whole universe…'
        : 'Run the batch scan for every tracked symbol. Skipped if today\'s scan already covers '
          + 'the newest data held.'}>
        <span>
          <Button
            variant={variant}
            size={size}
            disabled={busy}
            onClick={() => run(false)}
            startIcon={busy
              ? <CircularProgress size={16} color="inherit" />
              : <TravelExploreIcon />}
            sx={{ textTransform: 'none' }}
          >
            {busy ? 'Scanning…' : 'Run Global Scan'}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={Boolean(toast)}
        // An error stays until dismissed; a success or a skip auto-hides. A message that reports a
        // FAILURE must not disappear before it is read.
        autoHideDuration={toast?.severity === 'error' ? null : 6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast?.severity || 'info'}
          onClose={() => setToast(null)}
          action={toast?.allowForce ? (
            <Button color="inherit" size="small" onClick={() => run(true)}>
              Run anyway
            </Button>
          ) : undefined}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

GlobalScanButton.propTypes = {
  /** 1 = US, 2 = India. Omitted scans the backend's default market. */
  marketId: PropTypes.number,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  variant: PropTypes.string,
  size: PropTypes.string,
};

GlobalScanButton.defaultProps = { variant: 'outlined', size: 'small' };

export default GlobalScanButton;
